/**
 * Utility functions for app initialization and configuration
 */

import type { IMagicoAppData, MagicoColors } from "@models/AppConfigModels";
import { MAGICO_COLOR_KEYS } from "@models/AppConfigModels";
import { currentConfig } from "app.config";

// Supported languages
export const SUPPORTED_LANGUAGES = ["ar", "en"] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Validates and normalizes language input
 */
export const validateLanguage = (language: string): SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage) 
    ? (language as SupportedLanguage) 
    : "en";
};

/**
 * Converts hex color to RGB values (space-separated for Tailwind opacity support).
 * Returns null when the string is not a hex colour, so the caller can skip it.
 */
export const hexToRgb = (hex: string): string | null => {
  const cleanHex = hex.replace("#", "").trim();

  // Expand shorthand hex (#abc or #abcd)
  const fullHex =
    cleanHex.length === 3 || cleanHex.length === 4
      ? cleanHex.split("").map((c) => c + c).join("")
      : cleanHex;

  // Match #RRGGBB or #RRGGBBAA
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(fullHex);
  if (!result) {
    console.warn(`Could not parse hex color: ${hex}`);
    return null;
  }

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  // ⚠️ Ignore alpha here, since Tailwind handles it with /<alpha-value>
  return `${r} ${g} ${b}`;
};

/**
 * Writes each elmagico CMS colour to the CSS variable `--color-<key>`, using the
 * CMS key name unchanged so the mobile SDK, the widget and this portal share one
 * vocabulary. Values are stored as space-separated RGB channels because
 * `tailwind.config.ts` wraps them in `rgb(... / <alpha-value>)`.
 */
export const applyThemeColors = (colors: MagicoColors): void => {
  const root = document.documentElement;

  // A key that is not in MAGICO_COLOR_KEYS has no CSS variable behind it, so
  // writing it would be a silent no-op that looks like a working theme.
  for (const key of MAGICO_COLOR_KEYS) {
    const value = colors[key];
    if (!value) continue;

    // Writing an unparsable value here would delete the `:root` fallback and
    // leave the element with no colour at all, not with the default.
    const rgb = hexToRgb(value);
    if (!rgb) continue;

    root.style.setProperty(`--color-${key}`, rgb);
  }
};

/**
 * Development configuration for standalone mode
 */
export const getDevelopmentConfig = () => ({
  jwt: "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJpc3MiOiJHUklOVEEiLCJhdWQiOiJHQU1JRklDQVRJT04iLCJ1c2VySWQiOiIxMjM0NTQ2NyIsImlhdCI6MTc1ODA5NzQwNiwiZXhwIjoxNzk4MTAxMDA2fQ.B3cThM2VLyGl8SNvR1zx6bZQyfY6q3QuK9xeqOaDUg5IZ1_OZEk7h2fcJq1shAtoc8D5__ENGv2rpquh_OXSzaz4TZL-YTvpwp0YSMGjUM1wS_uV-IXwaRXEs6NYQOHhlfBdSljZjAGH4VUkEriG3zH8dptnJ046yqyIiHcv18qJqQDDNUjQsnkwq2Gzl73xXIcTyy_QKG3K4-nFcToBwk8yDxSkThVoBD5HoNAf7KYA8lz8yr5lGqf42v5zK2LHysXNxQpgO8xo55uGgNIU10a61-3LIEDVFVTQyUI4gyZoAEWS5AzZGNv-A18UXHtg-dkYvp9_5L6cjWa-LQUN0w",
  userId: "01207487904",
  clientId: currentConfig.DEFAULT_PROGRAME_KEY,
  appId: "154",
  language: "en" as SupportedLanguage
});

/**
 * Validates required configuration fields
 */
export const validateConfig = (config: unknown): boolean => {
  const cfg = config as Record<string, unknown>;
  return !!(cfg?.jwt && cfg?.userId && cfg?.clientId);
};


function loadFont(url: string) {
  if (document.querySelector(`link[href="${url}"]`)) return;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}

export const applyFonts = (configs: IMagicoAppData["configs"] ) => {
  if (configs.rtlFontLink) {
    loadFont(configs.rtlFontLink);
    document.documentElement.style.setProperty("--font-family-rtl", configs.rtlFontFamily ?? "");
  }
  
  if (configs.ltrFontLink){ 
    loadFont(configs.ltrFontLink);
    document.documentElement.style.setProperty("--font-family-ltr", configs.ltrFontFamily ?? "");
  }
};